import { computed, action, observable } from 'mobx';
import { ProductSortingService } from './ProductSortingService';
import { ProductFilterService } from './ProductFilterService';
import { IItem, ItemTypes, IItemsParams, IProductParams } from '../../../api/ProdcatApiSdk';
import { ProductRepository } from '../../repositories/ProductRepository';
import { Product } from '../entities/Product';
import { IContentItem } from '../../../exported/product-grid-wrapper/ProductGridWrapper';

export class ProductGridService {
    public productFilterService: ProductFilterService;
    public productSortingService: ProductSortingService;
    public productRepository: ProductRepository;
    @observable private content: IContentItem[] = [];
    @observable private productRepoAccessor: IItem[] = [];

    constructor(
        productFilterService: ProductFilterService,
        productSortingService: ProductSortingService,
        productRepository: ProductRepository
    ) {
        this.productFilterService = productFilterService;
        this.productSortingService = productSortingService;
        this.productRepository = productRepository;
    }

    @action
    public setProductAccessor(items: IItem[]) {
        this.productRepoAccessor = items;
    }

    @computed
    public get productAccessor() {
        return this.productRepoAccessor;
    }

    @action
    public setInlineContent(content: IContentItem[]) {
        this.content = content;
    }

    @computed
    public get inlineContent() {
        return this.content;
    }

    @computed
    public get products() {
        return this.accessedProducts;
    }

    @computed
    public get accessedProducts(): Product[] {
        return this.productRepoAccessor.reduce((sortedProducts: Product[], item: IItem) => {
            const { params, type } = item;

            const isSku = type === ItemTypes.sku;
            const isProduct = type === ItemTypes.product;

            const { id, skusToDisplay } = <IProductParams>params;

            const product = isSku
                ? this.productRepository.getProductBySkuId(id)
                : this.productRepository.getProductById(id);

            if (!!product) {
                const clonedProduct = Product.clone(product);
                if ((isProduct && skusToDisplay) || isSku) {
                    const filterMethod = isSku
                        ? ({ skuId }: { skuId: string }) => skuId === id
                        : ({ skuId }: { skuId: string }) => (skusToDisplay || []).includes(skuId);

                    clonedProduct.skus = clonedProduct.skus.filter(filterMethod);
                }
                if (clonedProduct.skus && clonedProduct.skus.length) {
                    sortedProducts.push(clonedProduct);
                }
            }

            return sortedProducts;
        }, []);
    }

    public getProductsByParams = async (itemParams: IItemsParams, productTags: string[]) => {
        const { items } = itemParams;

        if (items.length > 0) {
            this.setProductAccessor(items);
            await this.productRepository.getProductsByIds(items, true);
        } else if (productTags.length > 0) {
            const products = await this.productRepository.getProductsByTag(productTags);
            const tagItems: IItem[] = products.map(product => {
                return {
                    type: ItemTypes.product,
                    params: { id: product.productId }
                };
            });

            this.setProductAccessor(tagItems);
        }
    };
}
